import { useEffect, useState } from 'react';
import {
  GUARANTEED_JEV_TRIP_MIN_HITS_DEFAULT,
  GUARANTEED_JEV_TRIP_WINDOW_SECONDS_DEFAULT,
  GUARANTEED_PROBE_RECOVER_OK_ROUNDS_DEFAULT,
  GUARANTEED_PROBE_SHOTS_PER_ROUND_DEFAULT,
  GUARANTEED_PROBE_TRIP_FAILED_ROUNDS_DEFAULT,
  isGuaranteedJevMode,
  isGuaranteedJevTripMinHits,
  isGuaranteedJevTripWindowSeconds,
  isGuaranteedProbeRecoverOkRounds,
  isGuaranteedProbeShape,
  isGuaranteedProbeShotsPerRound,
  isGuaranteedProbeTripFailedRounds,
  type GuaranteedProbeShape,
  type GuaranteedServiceSkuEntry,
  type PatchGuaranteedServiceRequest,
} from '@nadoutong/contracts';
import { Trash2 } from 'lucide-react';
import { useT } from '../../i18n/index.ts';
import { IconButton, Input, Select } from '../../ui/index.ts';
import { isMode, MODE_VALUES, modeLabelKey, skuChainMode, skuFallbackMode, skuProbeMode } from './modes.ts';

/** Shared by the header row in ConfigCards so columns line up. */
export const SKU_GRID = 'grid grid-cols-[200px_minmax(330px,1.3fr)_minmax(140px,0.7fr)_minmax(180px,0.9fr)_160px_32px] gap-x-3';

function countOf(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function shownInt(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isSafeInteger(value) ? value : fallback;
}

function CommitNumber({
  value,
  disabled,
  testId,
  label,
  validate,
  onCommit,
}: {
  readonly value: number;
  readonly disabled: boolean;
  readonly testId: string;
  readonly label: string;
  readonly validate: (value: number) => boolean;
  readonly onCommit: (value: number) => void;
}): JSX.Element {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? String(value);
  return (
    <Input
      wrapperClassName="w-14"
      className="px-1.5 text-right"
      mono
      value={shown}
      disabled={disabled}
      inputMode="numeric"
      aria-label={label}
      data-testid={testId}
      onChange={(event) => setDraft(event.currentTarget.value)}
      onBlur={() => {
        const parsed = Number(shown);
        setDraft(null);
        if (!validate(parsed) || parsed === value) return;
        onCommit(parsed);
      }}
    />
  );
}

export function SkuConfigCard({
  row,
  onPatch,
  onEditNote,
  onDelete,
}: {
  readonly row: GuaranteedServiceSkuEntry;
  readonly onPatch: (body: PatchGuaranteedServiceRequest) => void;
  readonly onEditNote: () => void;
  readonly onDelete: () => void;
}): JSX.Element {
  const t = useT();
  const probeMode = skuProbeMode(row);
  const chainMode = skuChainMode(row);
  const fallbackMode = skuFallbackMode(row);
  const probesOff = probeMode === 'off';
  const modeOptions = MODE_VALUES.map((value) => ({ value, label: t(modeLabelKey(value)) }));
  const jev = isGuaranteedJevMode(row.jev_judge) ? row.jev_judge : 'off';
  const rowShape = isGuaranteedProbeShape(row.probe_shape) ? row.probe_shape : 'ping';
  const [shape, setShape] = useState<GuaranteedProbeShape>(rowShape);
  const [customText, setCustomText] = useState(row.probe_custom_text ?? '');
  const [customMax, setCustomMax] = useState(
    row.probe_custom_max_tokens === null || row.probe_custom_max_tokens === undefined
      ? ''
      : String(row.probe_custom_max_tokens),
  );
  const [customError, setCustomError] = useState<string | null>(null);
  useEffect(() => {
    setShape(rowShape);
    setCustomText(row.probe_custom_text ?? '');
    setCustomMax(
      row.probe_custom_max_tokens === null || row.probe_custom_max_tokens === undefined
        ? ''
        : String(row.probe_custom_max_tokens),
    );
  }, [rowShape, row.probe_custom_text, row.probe_custom_max_tokens]);

  function saveCustom(): void {
    const text = customText;
    if (text.trim().length === 0 || /[\u0000-\u001F\u007F-\u009F]/.test(text)) {
      setCustomError(text.trim().length === 0
        ? t('guaranteed.probe_custom_empty')
        : t('guaranteed.probe_custom_invalid'));
      return;
    }
    if (Array.from(text.trim()).length > 200) {
      setCustomError(t('guaranteed.probe_custom_invalid'));
      return;
    }
    const maxRaw = customMax.trim();
    let max: number | null = null;
    if (maxRaw.length > 0) {
      const parsed = Number(maxRaw);
      if (!Number.isSafeInteger(parsed) || parsed < 1 || parsed > 64) {
        setCustomError(t('guaranteed.probe_custom_max_invalid'));
        return;
      }
      max = parsed;
    }
    setCustomError(null);
    const trimmed = text.trim();
    if (
      row.probe_shape === 'custom'
      && trimmed === (row.probe_custom_text ?? '')
      && max === (row.probe_custom_max_tokens ?? null)
    ) return;
    onPatch({
      probe_shape: 'custom',
      probe_custom_text: trimmed,
      probe_custom_max_tokens: max,
    });
  }

  const shapeOptions = [
    { value: 'ping', label: t('guaranteed.probe_shape_ping') },
    { value: 'arithmetic', label: t('guaranteed.probe_shape_arithmetic') },
    { value: 'echo', label: t('guaranteed.probe_shape_echo') },
    { value: 'custom', label: t('guaranteed.probe_shape_custom') },
  ];

  return (
    <div
      className={`${SKU_GRID} items-start border-b border-line-soft px-3 py-3`}
      data-testid={`guaranteed-row-${row.id}`}
    >
      <div className="min-w-0 pt-0.5">
        <div className="truncate text-body font-medium text-paper" title={row.sku_name}>{row.sku_name}</div>
        <button
          type="button"
          className="mt-1 block max-w-full truncate text-left text-label text-paper-low"
          title={row.note ?? ''}
          data-testid={`guaranteed-note-${row.id}`}
          onClick={onEditNote}
        >
          {row.note ?? t('guaranteed.note_placeholder')}
        </button>
      </div>
      <div className="flex flex-col gap-2">
        <div>
          <span className="mb-1 block text-label text-paper-low">{t('guaranteed.probe.mode')}</span>
          <div className="flex flex-nowrap items-center gap-1.5 whitespace-nowrap">
            <Select
              value={probeMode}
              onValueChange={(value) => {
                if (isMode(value) && value !== probeMode) onPatch({ probe_mode: value });
              }}
              options={modeOptions}
              ariaLabel={t('guaranteed.probe.mode')}
              testId={`guaranteed-probe-enabled-${row.sku_id}`}
            />
            <Select
              value={shape}
              disabled={probesOff}
              onValueChange={(value) => {
                if (!isGuaranteedProbeShape(value) || value === shape) return;
                setShape(value);
                setCustomError(null);
                if (value === 'custom') return;
                onPatch({
                  probe_shape: value,
                  probe_custom_text: null,
                  probe_custom_max_tokens: null,
                });
              }}
              options={shapeOptions}
              ariaLabel={t('guaranteed.probe_shape')}
              testId={`guaranteed-probe-shape-${row.sku_id}`}
            />
          </div>
        </div>
        <div>
          <span className="mb-1 block text-label text-paper-low">{t('guaranteed.probe.interval')}</span>
          <div className="flex flex-nowrap items-center gap-1.5 whitespace-nowrap">
            <span className="text-micro text-paper-low">{t('guaranteed.probe.every')}</span>
            <CommitNumber
              value={shownInt(row.probe_interval_minutes, 30)}
              disabled={probesOff}
              testId={`guaranteed-probe-interval-${row.sku_id}`}
              label={t('guaranteed.probe.interval')}
              validate={(parsed) => Number.isSafeInteger(parsed) && parsed >= 1 && parsed <= 1440}
              onCommit={(parsed) => onPatch({ probe_interval_minutes: parsed })}
            />
            <span className="text-micro text-paper-low">{t('guaranteed.probe.minutes')}</span>
            <CommitNumber
              value={shownInt(row.probe_shots_per_round, GUARANTEED_PROBE_SHOTS_PER_ROUND_DEFAULT)}
              disabled={probesOff}
              testId={`guaranteed-probe-shots-${row.sku_id}`}
              label={t('guaranteed.probe.shots')}
              validate={isGuaranteedProbeShotsPerRound}
              onCommit={(parsed) => onPatch({ probe_shots_per_round: parsed })}
            />
            <span className="text-micro text-paper-low">{t('guaranteed.probe.shots')}</span>
          </div>
        </div>
        <div>
          <span className="mb-1 block text-label text-paper-low">{t('guaranteed.probe.criteria')}</span>
          <div className="flex flex-nowrap items-center gap-1.5 whitespace-nowrap">
            <span className="text-micro text-paper-low">{t('guaranteed.probe.consecutive')}</span>
            <CommitNumber
              value={shownInt(row.probe_trip_failed_rounds, GUARANTEED_PROBE_TRIP_FAILED_ROUNDS_DEFAULT)}
              disabled={probesOff}
              testId={`guaranteed-probe-fail-rounds-${row.sku_id}`}
              label={t('guaranteed.probe.fail_rounds')}
              validate={isGuaranteedProbeTripFailedRounds}
              onCommit={(parsed) => onPatch({ probe_trip_failed_rounds: parsed })}
            />
            <span className="text-micro text-paper-low">{t('guaranteed.probe.fail_rounds')}</span>
            <CommitNumber
              value={shownInt(row.probe_recover_ok_rounds, GUARANTEED_PROBE_RECOVER_OK_ROUNDS_DEFAULT)}
              disabled={probesOff}
              testId={`guaranteed-probe-pass-rounds-${row.sku_id}`}
              label={t('guaranteed.probe.pass_rounds')}
              validate={isGuaranteedProbeRecoverOkRounds}
              onCommit={(parsed) => onPatch({ probe_recover_ok_rounds: parsed })}
            />
            <span className="text-micro text-paper-low">{t('guaranteed.probe.pass_rounds')}</span>
          </div>
        </div>
        {shape === 'custom' ? (
          <div className="flex flex-col gap-2">
            <span className="text-label text-paper-low">{t('guaranteed.probe_custom_text')}</span>
            <Input
              value={customText}
              disabled={probesOff}
              onChange={(event) => {
                setCustomText(event.currentTarget.value);
                setCustomError(null);
              }}
              onBlur={() => saveCustom()}
              placeholder={t('guaranteed.probe_custom_text')}
              aria-label={t('guaranteed.probe_custom_text')}
              invalid={customError !== null}
              data-testid={`guaranteed-probe-custom-text-${row.sku_id}`}
            />
            <span className="text-label text-paper-low">{t('guaranteed.probe_custom_max')}</span>
            <Input
              value={customMax}
              disabled={probesOff}
              onChange={(event) => setCustomMax(event.currentTarget.value)}
              onBlur={() => saveCustom()}
              inputMode="numeric"
              aria-label={t('guaranteed.probe_custom_max')}
              placeholder="16"
              wrapperClassName="w-20"
              data-testid={`guaranteed-probe-custom-max-${row.sku_id}`}
            />
            {customError !== null ? (
              <span className="text-dense text-bad" data-testid={`guaranteed-probe-custom-error-${row.sku_id}`}>
                {customError}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="flex flex-col gap-2">
        <div>
          <span className="mb-1 block text-label text-paper-low">{t('guaranteed.chain_mode')}</span>
          <Select
            value={chainMode}
            onValueChange={(value) => {
              if (isMode(value) && value !== chainMode) onPatch({ chain_trip_mode: value });
            }}
            options={modeOptions}
            ariaLabel={t('guaranteed.chain_mode')}
            testId={`guaranteed-chain-trip-${row.sku_id}`}
          />
        </div>
        <div>
          <span className="mb-1 block text-label text-paper-low">{t('guaranteed.fallback_mode')}</span>
          <Select
            value={fallbackMode}
            onValueChange={(value) => {
              if (isMode(value) && value !== fallbackMode) onPatch({ ultimate_fallback: value });
            }}
            options={modeOptions}
            ariaLabel={t('guaranteed.fallback_mode')}
            testId={`guaranteed-fallback-${row.id}`}
          />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div>
          <span className="mb-1 block text-label text-paper-low">{t('guaranteed.jev.mode')}</span>
          <Select
            value={jev}
            onValueChange={(value) => {
              if (isGuaranteedJevMode(value) && value !== jev) onPatch({ jev_judge: value });
            }}
            options={modeOptions}
            ariaLabel={t('guaranteed.jev.mode')}
            testId={`guaranteed-jev-${row.id}`}
          />
        </div>
        <div>
          <span className="mb-1 block text-label text-paper-low">{t('guaranteed.jev.criteria')}</span>
          <div className="flex flex-nowrap items-center gap-1.5 whitespace-nowrap">
            <CommitNumber
              value={shownInt(row.jev_trip_min_hits, GUARANTEED_JEV_TRIP_MIN_HITS_DEFAULT)}
              disabled={jev === 'off'}
              testId={`guaranteed-jev-min-hits-${row.sku_id}`}
              label={t('guaranteed.jev_trip_min_hits')}
              validate={isGuaranteedJevTripMinHits}
              onCommit={(parsed) => onPatch({ jev_trip_min_hits: parsed })}
            />
            <span className="text-micro text-paper-low">{t('guaranteed.jev.hits')}</span>
            <CommitNumber
              value={shownInt(row.jev_trip_window_seconds, GUARANTEED_JEV_TRIP_WINDOW_SECONDS_DEFAULT)}
              disabled={jev === 'off'}
              testId={`guaranteed-jev-window-${row.sku_id}`}
              label={t('guaranteed.jev_trip_window_seconds')}
              validate={isGuaranteedJevTripWindowSeconds}
              onCommit={(parsed) => onPatch({ jev_trip_window_seconds: parsed })}
            />
            <span className="text-micro text-paper-low">{t('guaranteed.jev.seconds')}</span>
          </div>
        </div>
      </div>
      <div
        className="pt-0.5 text-right font-mono text-label tabular-nums leading-7 text-paper-mid"
        title={t('guaranteed.stats_hint')}
      >
        <div>
          {t('guaranteed.stat.requests')}{' '}
          <span className="font-medium text-paper">{countOf(row.request_count_24h)}</span>
        </div>
        <div>
          {t('guaranteed.stat.retry')}{' '}
          <span className="font-medium text-paper">{countOf(row.entered_chain_count_24h)}</span>
          {' · '}
          {t('guaranteed.stat.failed')}{' '}
          <span className="font-medium text-paper">{countOf(row.chain_exhausted_count_24h)}</span>
        </div>
        <div>
          {t('guaranteed.stat.shadow')}{' '}
          <span className="font-medium text-paper">{countOf(row.shadow_enter_24h)}</span>
        </div>
      </div>
      <IconButton
        label={t('guaranteed.delete')}
        size="sm"
        className="hover:text-bad"
        data-testid={`guaranteed-delete-${row.id}`}
        onClick={onDelete}
      >
        <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
      </IconButton>
    </div>
  );
}
